import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { LOCALES, passwordSchema } from "@jornado/shared";
import {
  ApiError, api, changePassword, einladungAnnehmen, login, logout, refresh, type SessionUser
} from "./api";
import { applyLocale } from "./i18n";
import { allesLoeschen } from "./datenbank";
import { eintraege } from "./warteschlange";
import { KundeNeu, KundeSeite, KundenListe } from "./kunden";
import { ObjektNeu, ObjektSeite, ObjekteListe } from "./objekte";
import BaukastenSeite from "./baukasten";
import { ObjektChecklisteSeite } from "./objektcheckliste";
import { AuftraegeListe, AuftragNeu, AuftragSeite } from "./auftraege";
import { EinstellungenSeite } from "./einstellungen";
import { UebernahmeSeite } from "./uebernahme";
import { ZeitenSeite } from "./zeiten";
import { MeinTagSeite } from "./meintag";
import { DienstplanSeite } from "./dienstplan";
import { AntraegeSeite } from "./antraege";
import { MeineZeitSeite } from "./meinezeit";
import { TeamSeite } from "./team";
import { FuhrparkSeite } from "./fuhrpark";
import { KostenSeite } from "./kosten";
import { HandbuchSeite } from "./handbuch";
import { HinweisSeite } from "./hinweis";
import { PruefungNeu, PruefungSeite, PruefungenListe } from "./pruefungen";
import { NotizenSeite } from "./notizen";
import { Rechtsleiste, Rechtsseite } from "./rechtliches";
import { Demobalken, Demoeinstieg } from "./demo";
import { Karte, Knopf, Passwortfeld } from "./ui";

/**
 * Geruest des Backoffice. Stufe 1 bringt Kunden und Objekte.
 * Die weiteren Bereiche kommen als eigene Routen dazu.
 */
export default function App() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);

  const [offeneAuftraege, setOffeneAuftraege] = useState(0);
  const [offeneAntraege, setOffeneAntraege] = useState(0);
  /*
   * Die eingereichten Berichte, die noch niemand freigegeben hat.
   *
   * Die Zahl stand bis zum 04.09.2026 als Kachel auf der Uebersicht. Sie war
   * das einzige an jener Seite, das taeglich zaehlte, und sie steht deshalb
   * jetzt am Reiter, wo sie ohne Umweg zu sehen ist.
   */
  const [offeneBerichte, setOffeneBerichte] = useState(0);
  const ort = useLocation();

  useEffect(() => {
    // Beim Laden still versuchen, die Sitzung fortzusetzen
    void refresh().then((u) => {
      if (u) {
        setUser(u);
        applyLocale(u.locale);
      }
      setChecking(false);
    });
  }, []);

  /*
   * Die Zahlen an den Reitern werden bei jedem Seitenwechsel neu geholt.
   *
   * Das sind kleine Abfragen und sie haben den Vorteil, dass die Zahl sofort
   * stimmt, nachdem jemand einen Auftrag erledigt hat. Ein eigener Zaehler,
   * der von Hand nachgefuehrt wird, laeuft frueher oder spaeter auseinander.
   */
  useEffect(() => {
    if (!user) return;
    void api.get<{ workOrders: unknown[] }>("/work-orders?offen=ja")
      .then((r) => setOffeneAuftraege(r.workOrders.length))
      .catch(() => undefined);
    void api.get<{ offen: number }>("/antraege?offen=ja")
      .then((r) => setOffeneAntraege(r.offen))
      .catch(() => undefined);
    void api.get<{ inspections: unknown[] }>("/inspections?status=submitted")
      .then((r) => setOffeneBerichte(r.inspections.length))
      .catch(() => undefined);
  }, [user, ort.pathname]);

  if (checking) {
    return <Centered><p className="text-leise">…</p></Centered>;
  }

  /*
   * Ein Einladungslink fuehrt an der Anmeldung vorbei. Wer eingeladen wurde,
   * hat noch kein Passwort und koennte sich also gar nicht anmelden.
   */
  const einladung = ort.pathname.startsWith("/einladung/")
    ? ort.pathname.slice("/einladung/".length)
    : null;

  if (!user && einladung) {
    return (
      <EinladungScreen
        token={einladung}
        onAngemeldet={(u) => { setUser(u); applyLocale(u.locale); }}
      />
    );
  }

  /*
   * Impressum und Datenschutzerklaerung stehen vor der Anmeldung.
   *
   * Art. 10 LSSI verlangt die Anbieterkennzeichnung staendig zugaenglich,
   * Art. 13 DSGVO die Unterrichtung im Zeitpunkt der Erhebung. Erhoben wird
   * die Mailadresse schon auf der Anmeldemaske, also muss die Erklaerung
   * davor erreichbar sein und nicht dahinter. Deshalb steht diese Abzweigung
   * vor der Pruefung, ob jemand angemeldet ist, und gilt fuer beide Faelle
   * gleich: eine zweite Fassung fuer Angemeldete waere eine zweite Fassung,
   * die auseinanderlaufen kann.
   */
  if (ort.pathname === "/datenschutz" || ort.pathname === "/impressum") {
    return <RechtsseiteRahmen art={ort.pathname === "/impressum" ? "impressum" : "datenschutz"} />;
  }

  if (!user) {
    return <LoginScreen onSuccess={(u) => { setUser(u); applyLocale(u.locale); }} />;
  }

  // Anlegen und Aendern ist der Geschaeftsleitung vorbehalten. Die Oberflaeche
  // blendet die Schaltflaechen nur aus, entschieden wird es in der API.
  const darfAendern = user.role === "admin";

  /**
   * Abmelden raeumt das Geraet auf.
   *
   * Alles, was ohne Netz gespeichert wurde, liegt in der Datenbank des
   * Browsers: Fotos vom Objekt, der Dienstplan, wartende Stempelungen. Wer
   * sich abmeldet, gibt das Geraet womoeglich weiter, und dann darf davon
   * nichts liegen bleiben.
   *
   * Wartet noch etwas auf Uebertragung, wird gefragt. Es geht dabei verloren,
   * und das soll niemand aus Versehen tun.
   */
  async function abmelden() {
    const wartend = await eintraege().catch(() => []);
    if (wartend.length > 0 && !window.confirm(t("netz.abmeldenTrotzWartend", { count: wartend.length }))) {
      return;
    }
    await logout();
    await allesLoeschen().catch(() => undefined);
    setUser(null);
  }

  return (
    <div className="min-h-screen">
      {/*
        Der Demohinweis steht ganz oben und ueber der Kopfzeile.

        Nicht darunter und nicht in einer Ecke: er soll auf jedem Bildschirm
        mit im Bild sein, den jemand einem Interessenten zeigt. Auf jeder
        gewoehnlichen Anlage zeichnet er nichts.
      */}
      <Demobalken />
      {/*
        Auf schmalen Geraeten ist neben Sprache, Passwort und Abmelden kein
        Platz mehr fuer den ausgeschriebenen Namen, er lief vorher in die
        Bedienelemente hinein. Dort traegt die Bildmarke allein, der Name
        erscheint ab der Tabletbreite.
      */}
      {/*
        Am Telefon nimmt die Kopfzeile so wenig Platz wie moeglich.

        Vorher standen Bildmarke, Sprache, Passwort und Abmelden in voller
        Groesse ueber jeder Maske, dazu die Reiterleiste: zusammen ein gutes
        Viertel des Bildschirms, bevor der erste Termin kam. Die Marke ist
        jetzt kleiner, die Zeile enger, und die Reiter ruecken zusammen. Ab
        Tabletbreite bleibt alles wie es war, dort ist der Platz da.
      */}
      <header className="border-b-2 border-marke bg-white">
        <div className="flex items-center justify-between gap-3 px-4 py-2 sm:px-6 sm:py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Bildmarke className="h-8 w-auto shrink-0 sm:h-12" />
            <span className="hidden truncate font-bold uppercase tracking-[0.2em] sm:inline">
              {t("app.name")}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-4">
            <select
              className="min-h-11 rounded border border-linie/40 bg-white px-2 py-1 text-sm"
              value={i18n.language}
              aria-label={t("kunden.sprache")}
              onChange={(e) => applyLocale(e.target.value)}
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </select>
            <button
              className="inline-flex min-h-11 items-center text-sm text-leise underline"
              onClick={() => setPwOpen(true)}
            >
              {t("nav.password")}
            </button>
            <button
              className="inline-flex min-h-11 items-center text-sm text-leise underline"
              onClick={() => void abmelden()}
            >
              {t("nav.logout")}
            </button>
          </div>
        </div>

        {/*
          Zwei ganz verschiedene Navigationen an derselben Stelle.

          Ein Mitarbeiter bekommt zwei Reiter: seinen Tag und seine Woche.
          Alles, was er braucht, liegt in diesen beiden. Ihm die zehn Reiter
          des Bueros hinzustellen hiesse, ihn jeden Morgen zwischen Dingen
          waehlen zu lassen, die ihn nichts angehen, und die Fehlerquelle
          waere die Auswahl selbst.

          Die Geschaeftsleitung bekommt das Buero.
        */}
        <nav className="flex gap-1 overflow-x-auto px-2 sm:px-4">
          {darfAendern ? (
            <>
              {/*
                Die Reihenfolge ist der Arbeitstag der Geschaeftsleitung und
                nicht die Ordnung eines Menues.

                Die Leiste scrollt waagerecht. Am Telefon sind nur die ersten
                Reiter zu sehen, und was dort steht, wird ohne Wischen erreicht.
                Deshalb stehen vorn die acht, die taeglich drankommen: der
                eigene Tag, der Plan, die Berichte, was hereinkommt, die Zeiten
                und der Baukasten. Alles Weitere ist Nachschlagen und
                Verwaltung und darf hinten stehen.
              */}
              {/* Auch die Geschaeftsleitung faehrt raus und stempelt. Ohne
                  diesen Reiter kaeme sie an ihren eigenen Tag nicht heran. */}
              <Reiter zu="/mein-tag" text={t("nav.meinTag")} />
              <Reiter zu="/plan" text={t("nav.plan")} />
              <Reiter zu="/pruefungen" text={t("nav.pruefungen")} zahl={offeneBerichte} />
              {/* Die internen Notizen aus den Berichten. Sie steht direkt
                  hinter den Berichten, weil sie aus ihnen kommt, und vor den
                  Auftraegen, weil aus einer Notiz oft einer wird. */}
              <Reiter zu="/notizen" text={t("nav.notizen")} />
              <Reiter zu="/auftraege" text={t("nav.auftraege")} zahl={offeneAuftraege} />
              <Reiter zu="/zeiten" text={t("nav.zeiten")} />
              <Reiter zu="/baukasten" text={t("nav.baukasten")} />
              {/*
                Objekte haben keinen eigenen Reiter mehr. Jedes Objekt gehoert
                genau einem Kunden, und das Kundenblatt fuehrt es bereits auf.
                Ein zweiter Weg zur selben Akte macht die Leiste laenger, ohne
                etwas zu koennen, was der erste nicht kann. Die Objektliste
                selbst bleibt: sie traegt den Koordinatenhelfer und den Filter
                fuer fehlende Zugangsdaten und haengt als "Alle Objekte" an der
                Kundenliste.
              */}
              <Reiter zu="/kunden" text={t("nav.customers")} />
              <Reiter zu="/antraege" text={t("nav.antraege")} zahl={offeneAntraege} />
              <Reiter zu="/team" text={t("nav.team")} />
              <Reiter zu="/fuhrpark" text={t("nav.fuhrpark")} />
              <Reiter zu="/kosten" text={t("nav.kosten")} />
              <Reiter zu="/handbuch" text={t("nav.handbuch")} />
              <Reiter zu="/einstellungen" text={t("nav.einstellungen")} />
              <Reiter zu="/uebernahme" text={t("nav.uebernahme")} />
            </>
          ) : (
            <>
              {/*
                Die Reihenfolge folgt dem Tag eines Mitarbeiters und nicht der
                Ordnung des Bueros: erst der Tag, dann was unterwegs anfaellt,
                dann die eigene Woche und die eigenen Zahlen, zuletzt das
                Nachschlagen.
              */}
              <Reiter zu="/" text={t("nav.meinTag")} />
              <Reiter zu="/fuhrpark" text={t("nav.fuhrpark")} />
              <Reiter zu="/kosten" text={t("nav.kosten")} />
              <Reiter zu="/plan" text={t("nav.meineWoche")} />
              <Reiter zu="/meine-zeit" text={t("nav.meineZeit")} />
              <Reiter zu="/antraege" text={t("nav.antraege")} />
              <Reiter zu="/handbuch" text={t("nav.handbuch")} />
              <Reiter zu="/hinweis" text={t("nav.hinweis")} />
            </>
          )}
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Routes>
          {/*
            Der eigene Tag ist die Startseite, fuer alle.

            Bis zum 04.09.2026 stand hier fuer die Geschaeftsleitung eine
            Uebersicht: eine Begruessung, zwei Zaehler und drei Tabellen. Sie
            beantwortete keine Frage, die der Tag nicht auch beantwortet, und
            sie kostete beim Anmelden vier Abfragen. Was an ihr Wert hatte,
            steht jetzt dort, wo es hingehoert: die Zahl der offenen Berichte
            am Reiter, der Wasserverlauf an der Objektliste, Einrichtung und
            Anlagenzustand in den Einstellungen.
          */}
          <Route path="/" element={<MeinTagSeite user={user} />} />
          <Route path="/mein-tag" element={<MeinTagSeite user={user} />} />
          <Route path="/plan" element={<DienstplanSeite user={user} />} />
          <Route path="/meine-zeit" element={<MeineZeitSeite />} />
          <Route path="/antraege" element={<AntraegeSeite user={user} />} />
          <Route path="/fuhrpark" element={<FuhrparkSeite user={user} />} />
          <Route path="/kosten" element={<KostenSeite user={user} />} />
          <Route path="/handbuch" element={<HandbuchSeite user={user} />} />
          <Route path="/hinweis" element={<HinweisSeite />} />
          {/* Wer den Einladungslink oeffnet und schon angemeldet ist, soll
              nicht auf einer leeren Seite landen. */}
          <Route path="/einladung/*" element={<MeinTagSeite user={user} />} />
          {/*
            Die Bueroseiten sind gesperrt, nicht nur ausgeblendet.

            Ein Reiter, den man nicht sieht, ist keine Schranke: die Adresse
            laesst sich tippen. Entschieden wird das ohnehin in der API, aber
            eine Oberflaeche, die eine Seite oeffnet und dann an leeren
            Fehlermeldungen scheitert, sieht kaputt aus statt verboten.
          */}
          <Route path="/kunden" element={<NurBuero darf={darfAendern}><KundenListe darfAendern={darfAendern} /></NurBuero>} />
          <Route path="/kunden/neu" element={<NurBuero darf={darfAendern}><KundeNeu /></NurBuero>} />
          <Route path="/kunden/:id" element={<NurBuero darf={darfAendern}><KundeSeite darfAendern={darfAendern} /></NurBuero>} />
          <Route path="/objekte" element={<ObjekteListe darfAendern={darfAendern} />} />
          <Route path="/objekte/neu" element={<NurBuero darf={darfAendern}><ObjektNeu /></NurBuero>} />
          <Route path="/objekte/:id" element={<ObjektSeite darfAendern={darfAendern} />} />
          <Route path="/pruefungen" element={<PruefungenListe />} />
          <Route path="/pruefungen/neu" element={<PruefungNeu />} />
          <Route path="/pruefungen/:id" element={<PruefungSeite darfFreigeben={darfAendern} />} />
          <Route path="/notizen" element={<NurBuero darf={darfAendern}><NotizenSeite /></NurBuero>} />
          <Route path="/auftraege" element={<AuftraegeListe />} />
          <Route path="/auftraege/neu" element={<NurBuero darf={darfAendern}><AuftragNeu /></NurBuero>} />
          <Route path="/auftraege/:id" element={<AuftragSeite />} />
          <Route path="/zeiten" element={<ZeitenSeite user={user} />} />
          <Route path="/baukasten" element={<NurBuero darf={darfAendern}><BaukastenSeite darfAendern={darfAendern} /></NurBuero>} />
          <Route path="/einstellungen" element={<NurBuero darf={darfAendern}><EinstellungenSeite darfAendern={darfAendern} /></NurBuero>} />
          <Route path="/uebernahme" element={<NurBuero darf={darfAendern}><UebernahmeSeite /></NurBuero>} />
          <Route path="/team" element={<NurBuero darf={darfAendern}><TeamSeite /></NurBuero>} />
          <Route path="/checklisten/:id" element={<ObjektChecklisteSeite darfAendern={darfAendern} />} />
          <Route path="*" element={<p className="text-leise">{t("fehler.seite")}</p>} />
        </Routes>
      </main>

      {pwOpen && <PasswordDialog onClose={() => setPwOpen(false)} onChanged={() => setUser(null)} />}
    </div>
  );
}

/**
 * Ein Reiter, wahlweise mit einer Zahl daneben.
 *
 * Die Zahl steht an Auftraegen und Antraegen. Ohne sie faellt ein neuer
 * Vorgang nur im Postfach auf, und wer den Reiter nicht von sich aus
 * anklickt, erfaehrt nie davon.
 */
/**
 * Waechter fuer die Bueroseiten.
 *
 * Sagt klar, dass die Seite nicht fuer den Anmelder gedacht ist, statt eine
 * halb geladene Maske mit Fehlermeldungen zu zeigen. Die eigentliche
 * Entscheidung faellt in der API; das hier ist Hoeflichkeit, keine Sicherheit.
 */
function NurBuero({ darf, children }: { darf: boolean; children: React.ReactNode }) {
  const { t } = useTranslation();
  if (darf) return <>{children}</>;
  return (
    <Karte className="p-6">
      <p className="font-semibold">{t("rechte.nurBuero")}</p>
      <p className="mt-1 text-sm text-leise">{t("rechte.nurBueroHinweis")}</p>
      <div className="mt-4">
        <Link to="/" className="text-sm font-semibold text-verweis underline">
          {t("rechte.zurueck")}
        </Link>
      </div>
    </Karte>
  );
}

function Reiter({ zu, text, zahl = 0 }: { zu: string; text: string; zahl?: number }) {
  return (
    <NavLink
      to={zu}
      end={zu === "/"}
      className={({ isActive }) =>
        `flex min-h-11 items-center gap-2 whitespace-nowrap border-b-2 px-2 text-sm font-semibold transition sm:px-3 ${
          isActive ? "border-marke text-tinte" : "border-transparent text-leise hover:text-tinte"
        }`
      }
    >
      {text}
      {zahl > 0 && (
        <span className="rounded-full bg-marke px-2 py-0.5 text-xs font-bold text-white">{zahl}</span>
      )}
    </NavLink>
  );
}

/**
 * Die Einladung annehmen.
 *
 * Die Fehlermeldung unterscheidet zwei Faelle, die beide mit 400 antworten:
 * ein untauglicher Link und ein zu schwaches Passwort. Ohne die Unterscheidung
 * liest der neue Kollege "Einladung abgelaufen", obwohl sie gilt, fordert eine
 * neue an und scheitert wieder an derselben Stelle.
 */
function EinladungScreen({
  token, onAngemeldet
}: { token: string; onAngemeldet: (u: SessionUser) => void }) {
  const { t } = useTranslation();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (pw1 !== pw2) {
      setFehler(t("einladung.ungleich"));
      return;
    }
    // Vor dem Absenden pruefen, damit ein zu kurzes Passwort nicht als
    // abgelaufener Link zurueckkommt.
    if (!passwordSchema.safeParse(pw1).success) {
      setFehler(t("einladung.hinweis"));
      return;
    }
    setLaeuft(true);
    try {
      onAngemeldet(await einladungAnnehmen(token, pw1));
    } catch (err) {
      setFehler(
        err instanceof ApiError && err.code === "invitation_invalid"
          ? t("einladung.ungueltig")
          : err instanceof ApiError ? err.message : t("einladung.ungueltig")
      );
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Centered>
      <form onSubmit={absenden} className="w-full max-w-sm rounded-card bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold">{t("einladung.titel")}</h1>
        <p className="mt-1 text-sm text-leise">{t("einladung.hinweis")}</p>

        <label className="mb-1 mt-4 block text-sm font-medium">{t("einladung.passwort")}</label>
        <Passwortfeld
          wert={pw1} onWert={setPw1} autoComplete="new-password" required={false}
          zeigen={t("login.zeigen")} verbergen={t("login.verbergen")}
        />

        <label className="mb-1 mt-3 block text-sm font-medium">{t("einladung.wiederholen")}</label>
        <Passwortfeld
          wert={pw2} onWert={setPw2} autoComplete="new-password" required={false}
          zeigen={t("login.zeigen")} verbergen={t("login.verbergen")}
        />

        {fehler && <p className="mt-3 text-sm text-red-700">{fehler}</p>}

        <button
          type="submit" disabled={laeuft || !pw1}
          className="mt-5 w-full rounded bg-marke px-4 py-2 font-semibold text-white transition hover:bg-marke-dunkel disabled:opacity-60"
        >
          {t("einladung.starten")}
        </button>
      </form>
    </Centered>
  );
}

/**
 * Passwort aendern. Nach dem Wechsel werden serverseitig alle Sitzungen
 * beendet, deshalb landet man danach wieder auf der Anmeldemaske.
 */
function PasswordDialog({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await changePassword(current, next);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("password.failed"));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-card bg-white p-6 shadow-lg">
        <h2 className="mb-1 text-lg font-bold">{t("password.title")}</h2>
        <p className="mb-4 text-sm text-leise">{t("password.hint")}</p>

        <label className="mb-1 block text-sm font-medium">{t("password.current")}</label>
        <Passwortfeld
          wert={current} onWert={setCurrent} autoComplete="current-password"
          zeigen={t("login.zeigen")} verbergen={t("login.verbergen")}
          className="mb-4"
        />

        <label className="mb-1 block text-sm font-medium">{t("password.new")}</label>
        <Passwortfeld
          wert={next} onWert={setNext} autoComplete="new-password" minLength={12}
          zeigen={t("login.zeigen")} verbergen={t("login.verbergen")}
          className="mb-1"
        />
        <p className="mb-4 text-xs text-leise">{t("password.rule")}</p>

        {error && <p className="mb-4 text-sm text-red-700">{error}</p>}

        <div className="flex gap-3">
          <Knopf type="button" art="neben" onClick={onClose}>{t("password.cancel")}</Knopf>
          <Knopf type="submit" disabled={pending}>
            {pending ? t("login.pending") : t("password.save")}
          </Knopf>
        </div>
      </form>
    </div>
  );
}

function LoginScreen({ onSuccess }: { onSuccess: (u: SessionUser) => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      onSuccess(await login(email, password));
    } catch (err) {
      // Eine Sperre nach zu vielen Fehlversuchen ist etwas anderes als ein
      // falsches Passwort. Beides gleich zu benennen kostet nur Suchzeit.
      // Bei allen anderen Fehlern bleibt es beim neutralen Text, damit
      // niemand daraus ablesen kann, ob es die Adresse ueberhaupt gibt.
      setError(err instanceof ApiError && err.status === 429 ? err.message : t("login.failed"));
    } finally {
      setPending(false);
    }
  }

  return (
    <Centered>
      {/*
        Die Rechtsverweise stehen unter der Maske und nicht irgendwo tiefer im
        angemeldeten Bereich: hier wird die Mailadresse eingegeben, und genau
        hier verlangt Art. 13 DSGVO die Unterrichtung.
      */}
      <div className="w-full max-w-sm">
      <form onSubmit={submit} className="w-full rounded-card bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center">
          <Wortbildmarke />
        </div>
        <h1 className="mb-6 text-center text-xl font-bold">{t("login.title")}</h1>

        <label className="mb-1 block text-sm font-medium">{t("login.email")}</label>
        <input
          type="email" required autoComplete="username" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4 w-full rounded border border-linie/40 px-3 py-2 outline-none focus:border-marke"
        />

        <label className="mb-1 block text-sm font-medium">{t("login.password")}</label>
        <Passwortfeld
          wert={password} onWert={setPassword} autoComplete="current-password"
          zeigen={t("login.zeigen")} verbergen={t("login.verbergen")}
          className="mb-6"
        />

        {error && <p className="mb-4 text-sm text-red-700">{error}</p>}

        <button
          type="submit" disabled={pending}
          className="w-full rounded bg-marke py-2 font-semibold text-white transition hover:bg-marke-dunkel disabled:opacity-60"
        >
          {pending ? t("login.pending") : t("login.submit")}
        </button>
      </form>
      <Demoeinstieg onSuccess={onSuccess} />
      <Rechtsleiste className="mt-6" />
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-6">{children}</div>;
}

/**
 * Der Rahmen um Impressum und Datenschutzerklaerung.
 *
 * Bewusst eigenstaendig und nicht in der angemeldeten Oberflaeche: die Seiten
 * muessen ohne Anmeldung dastehen, und eine Reiterleiste, die zu gesperrten
 * Seiten fuehrt, waere fuer einen Leser ohne Zugang nur eine Reihe von
 * Sackgassen. Der Weg zurueck ist ein gewoehnlicher Verweis und laedt die
 * Anwendung neu, damit auch der Angemeldete wieder dort landet, wo er war.
 */
function RechtsseiteRahmen({ art }: { art: "datenschutz" | "impressum" }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen">
      <header className="border-b-2 border-marke bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-2 sm:px-6 sm:py-3">
          <Bildmarke className="h-8 w-auto shrink-0" />
          <a href="/" className="text-sm font-semibold text-verweis underline">{t("recht.zurueck")}</a>
        </div>
      </header>
      <Rechtsseite art={art} />
      <Rechtsleiste className="pb-10" />
    </div>
  );
}

/**
 * Signet der Marke, aus dem Markenkit als SVG. Liegt unter public und wird
 * deshalb unveraendert ausgeliefert. Rein dekorativ, der Firmenname steht
 * daneben als Text.
 */
function Bildmarke({ className = "h-12 w-auto shrink-0" }: { className?: string }) {
  return <img src="/signet.svg" alt="" aria-hidden="true" className={className} />;
}

/** Vollstaendiges Logo mit Schriftzug, Signet und Claim, aus dem Markenkit als SVG. */
function Wortbildmarke({ className = "w-56 max-w-full" }: { className?: string }) {
  return <img src="/wortbildmarke.svg" alt="Jornado" className={className} />;
}
